import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

export default function Anatomy() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [arch, setArch] = useState("All");

  useEffect(() => {
    api.get("/anatomy").then(({ data }) => {
      setItems(data);
      if (data.length > 0) setSelected(data[0]);
    });
  }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => arch === "All" || i.arch === arch);
  }, [items, arch]);

  return (
    <div className="bg-[#0A0A0A] text-[#F9F8F6] min-h-[calc(100vh-4rem)]" data-testid="anatomy-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-8">
            <div className="font-mono-label text-[#FF3B30]">Clinical Reference</div>
            <h1 className="font-serif-display text-5xl md:text-6xl mt-3 font-light leading-none tracking-tighter">
              Anatomy library
            </h1>
            <p className="mt-4 text-white/60 max-w-2xl">
              FDI-indexed tooth reference with morphological features, cusps, ridges, and characteristic landmarks.
            </p>
          </div>
          <div className="lg:col-span-4 flex gap-2 items-end">
            {["All", "Maxillary", "Mandibular"].map((a) => (
              <button
                key={a}
                onClick={() => setArch(a)}
                data-testid={`anatomy-filter-${a.toLowerCase()}`}
                className={`font-mono-label px-3 py-2 border whitespace-nowrap ${
                  arch === a ? "bg-[#FF3B30] text-black border-[#FF3B30]" : "bg-transparent border-white/20 hover:border-white"
                }`}
                style={{ borderRadius: 2 }}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-4 border border-white/10" style={{ borderRadius: 2 }}>
            <div className="font-mono-label text-white/40 px-4 py-3 border-b border-white/10">TEETH · {filtered.length}</div>
            <div className="max-h-[560px] overflow-y-auto">
              {filtered.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  data-testid={`anatomy-item-${a.fdi}`}
                  className={`w-full text-left p-4 border-b border-white/10 flex gap-4 items-center transition-colors ${
                    selected?.id === a.id ? "bg-white/5" : "hover:bg-white/5"
                  }`}
                >
                  <div className="font-mono text-3xl font-light w-14">{a.fdi}</div>
                  <div className="flex-1">
                    <div className="font-serif-display text-lg leading-none">{a.name}</div>
                    <div className="font-mono-label text-white/50 mt-2">{a.arch} · {a.type}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          {selected && (
            <div className="lg:col-span-8 border border-white/10 p-8" style={{ borderRadius: 2 }} data-testid="anatomy-detail">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="font-mono-label text-[#FF3B30]">FDI {selected.fdi} · Universal {selected.universal}</div>
                  <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-none tracking-tighter">{selected.name}</h2>
                  <div className="font-mono-label text-white/50 mt-4">{selected.arch} · {selected.type}</div>
                  <p className="mt-6 text-white/70 leading-relaxed">{selected.description}</p>
                </div>
                <div>
                  <img src={selected.image} alt={selected.name} className="w-full h-64 object-cover grayscale contrast-125" style={{ borderRadius: 2 }} />
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10">
                <div className="font-mono-label text-white/40 mb-4">Key Morphological Features</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selected.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm" data-testid={`anatomy-feature-${i}`}>
                      <span className="font-mono text-[#FF3B30] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-white/80">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
